import { FC } from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { Trans, useT } from '@clara/locale'
import { Button, useSnackbar } from '@clara/ui'
import {
  SidePanelObject,
  SidePanelElementProps,
  useSidePanelContext,
} from '@clara/layout-next'

import MarkAsPaidWarning from 'assets/images/mark-as-paid-warning.svg'
import ReimbursementStatusChip from '@/components/reimbursement-status-chip'
import { OPERATION_COUNTRY } from '@/config/env'

type Props = {
  onContinue: VoidFunction
}

const ExistingReimbursementsWarningSidepanel: FC<
  SidePanelElementProps & Props
> = ({ onContinue }) => {
  const { t } = useT('reimbursements')
  const snackbar = useSnackbar()
  const sidePanel = useSidePanelContext()

  return (
    <Stack>
      <Stack sx={{ alignItems: 'center' }}>
        <img src={MarkAsPaidWarning} alt="update bank information warning" />
      </Stack>
      <Typography
        variant="h2"
        sx={(theme) => ({
          mt: 4,
          fontWeight: 600,
          color: theme.palette.v3.content.high_emphasis,
        })}
      >
        {t(`bankinfowarning.title`)}
      </Typography>
      <Typography
        variant="body2"
        sx={(theme) => ({
          mt: 1,
          fontWeight: 400,
          color: theme.palette.v3.content.medium_emphasis,
        })}
      >
        <Trans
          t={t}
          i18nKey={`bankinfowarning.description`}
          components={{
            br: <br />,
            b: <b />,
            box: <Box sx={{ mt: 1, gap: 1, display: 'flex' }} />,
            pending: (
              <ReimbursementStatusChip status="PENDING" paymentMethod="DEBIT" />
            ),
            approved: (
              <ReimbursementStatusChip
                status="APPROVED"
                paymentMethod="DEBIT"
              />
            ),
            // TODO: enable this when payments works in other countries
            inprogress:
              OPERATION_COUNTRY === 'MX' ? (
                <ReimbursementStatusChip
                  status="PAYMENT_IN_PROGRESS"
                  paymentMethod="DEBIT"
                />
              ) : (
                <></>
              ),
            paid: (
              <ReimbursementStatusChip status="PAID" paymentMethod="DEBIT" />
            ),
            rejected: (
              <ReimbursementStatusChip
                status="REJECTED"
                paymentMethod="DEBIT"
              />
            ),
          }}
        />
      </Typography>
      <Typography
        variant="body1"
        sx={(theme) => ({
          mt: 2,
          fontWeight: 600,
          color: theme.palette.v3.content.high_emphasis,
        })}
      >
        {t(`disable.sidepanel.continue`)}
      </Typography>
      <Stack
        direction="row"
        gap={2}
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'center',
          position: 'absolute',
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(3),
          left: theme.spacing(3),
          right: theme.spacing(3),
          bottom: 0,
          backgroundColor: theme.palette.common.white,
          zIndex: 1,
        })}
      >
        <Button
          sx={(theme) => ({ minWidth: 200, marginTop: theme.spacing(1) })}
          variant="secondary"
          onClick={() => {
            sidePanel.prev()
          }}
        >
          {t('disable.sidepanel.no')}
        </Button>
        <Button
          sx={(theme) => ({ minWidth: 200, marginTop: theme.spacing(1) })}
          onClick={async () => {
            try {
              onContinue()
            } catch (error) {
              snackbar({
                message: t('bankinfo.update.error'),
                status: 'error',
                isClosable: true,
              })
              sidePanel.panels = []
              sidePanel.onClose()
            }
          }}
        >
          {t('disable.sidepanel.yes')}
        </Button>
      </Stack>
    </Stack>
  )
}

export const getExistingReimbursementsWarningSidePanel = ({
  onContinue,
}: Props) => {
  return [
    {
      children: [
        {
          element: (
            <ExistingReimbursementsWarningSidepanel onContinue={onContinue} />
          ),
        },
      ],
    },
  ] as SidePanelObject[]
}
